import { Command } from "commander";
import chalk from "chalk";
import ora from "ora";
import * as fs from "fs";
import * as path from "path";
import * as os from "os";
import { PulseProtocol, VERSION } from "../index.js";
import { printVerdict } from "./print.js";
import { runDaemon } from "../daemon/index.js";

const program = new Command();

program
  .name("pulse")
  .description("Pulse Protocol — fake agents have no pulse")
  .version(VERSION);

program
  .command("scan")
  .description("Scan a target and return a verdict")
  .requiredOption("--handle <handle>", "X / Twitter handle (no @)")
  .requiredOption("--wallet <wallet>", "Solana wallet address")
  .option("--out <format>", "Output format: pretty | json | verdict-only", "pretty")
  .option("--no-post", "Do not publish verdict to the public feed")
  .action(async (opts) => {
    const config = loadConfig();
    const pulse = new PulseProtocol(config);

    const spinner = ora({
      text: chalk.magenta(`scanning @${opts.handle}...`),
      color: "magenta",
    }).start();

    try {
      const verdict = await pulse.scan(
        { handle: opts.handle, wallet: opts.wallet },
        { noPost: !opts.post }
      );
      spinner.stop();
      printVerdict(verdict, opts.out);
    } catch (err) {
      spinner.fail(chalk.red((err as Error).message));
      process.exit(1);
    }
  });

program
  .command("watch")
  .description("Start a watchlist daemon that re-scans targets on an interval")
  .option("--config <path>", "Path to watchlist YAML")
  .option("--interval <duration>", "Scan interval", "6h")
  .option("--webhook <url>", "POST verdict changes to this URL")
  .action(async (opts) => {
    const config = loadConfig();
    const watchlistPath =
      opts.config ?? path.join(os.homedir(), ".config/pulse/watchlist.yaml");

    if (!fs.existsSync(watchlistPath)) {
      console.error(chalk.red(`watchlist not found: ${watchlistPath}`));
      console.log(chalk.gray(`create one with:`));
      console.log(chalk.gray(`  targets:\n    - handle: foo\n      wallet: abc...`));
      process.exit(1);
    }

    await runDaemon({
      config,
      watchlistPath,
      interval: opts.interval,
      webhook: opts.webhook,
    });
  });

program
  .command("init")
  .description("Initialize ~/.config/pulse/.env with empty keys")
  .action(() => {
    const dir = path.join(os.homedir(), ".config/pulse");
    fs.mkdirSync(dir, { recursive: true });
    const envPath = path.join(dir, ".env");
    if (fs.existsSync(envPath)) {
      console.log(chalk.yellow(`already exists: ${envPath}`));
      return;
    }
    fs.writeFileSync(
      envPath,
      `# Pulse Protocol configuration\nHELIUS_API_KEY=\nX_API_KEY=\n`
    );
    console.log(chalk.green(`created ${envPath}`));
    console.log(chalk.gray(`fill in your API keys, then run: pulse scan --help`));
  });

program
  .command("feed")
  .description("Stream the public verdict feed")
  .action(() => {
    console.log(chalk.magenta("streaming public feed..."));
    console.log(chalk.gray("(connect to wss://feed.pulseprotocol.tech/ws)"));
  });

program
  .command("verify <verdictId>")
  .description("Verify the signature on a public verdict")
  .action((verdictId: string) => {
    console.log(chalk.magenta(`verifying ${verdictId}...`));
    console.log(chalk.gray("(fetches from https://pulseprotocol.tech/v/)"));
  });

function loadConfig() {
  const envPath = path.join(os.homedir(), ".config/pulse/.env");
  if (fs.existsSync(envPath)) {
    const env = fs.readFileSync(envPath, "utf8");
    for (const line of env.split("\n")) {
      const match = line.match(/^([A-Z_]+)=(.*)$/);
      if (match && match[1] && !process.env[match[1]]) {
        process.env[match[1]] = match[2];
      }
    }
  }

  const heliusApiKey = process.env.HELIUS_API_KEY;
  const xApiKey = process.env.X_API_KEY;

  if (!heliusApiKey || !xApiKey) {
    console.error(chalk.red("missing HELIUS_API_KEY or X_API_KEY"));
    console.error(chalk.gray("run: pulse init"));
    process.exit(1);
  }

  return { heliusApiKey, xApiKey };
}

program.parse(process.argv);
